import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { FONTS, SPACING, RADIUS, COLORS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { isCoveragePartnerBrandingVisible } from '@/lib/config/insurance-partners'

type Variant = 'horizontal' | 'mark' | 'inline'

interface InsurerBadgeProps {
  variant: Variant
  width?: number
  testID?: string
}

const DEFAULT_WIDTHS: Record<Variant, number> = {
  horizontal: 116,
  mark: 64,
  inline: 58,
}

export function InsurerBadge({ variant, width, testID }: InsurerBadgeProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  // Con la cobertura live, la insignia anuncia la póliza real del viaje; en
  // cualquier otro estado mantiene el registro genérico "incluida".
  const coverageLive = isCoveragePartnerBrandingVisible()
  const a11yLabel = coverageLive
    ? t('insurance.provider_partner_live_a11y')
    : t('insurance.provider_launching_soon_a11y')

  const imageWidth = width ?? DEFAULT_WIDTHS[variant]
  const markTestID = testID ? `${testID}.coverage-mark` : 'insurer-badge.coverage-mark'

  if (variant === 'inline') {
    return (
      <View testID={testID} style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
        <CoverageMark width={8} height={8} testID={markTestID} />
        <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
          {t('insurance.provider_short_included_soon')}
        </Text>
      </View>
    )
  }

  if (variant === 'mark') {
    return (
      <View
        testID={testID}
        style={{
          width: imageWidth,
          height: imageWidth,
          alignItems: 'center',
          justifyContent: 'center',
        }}
        accessibilityRole="image"
        accessibilityLabel={a11yLabel}
      >
        <CoverageMark width={10} height={10} testID={markTestID} />
      </View>
    )
  }

  return (
    <View
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        width: '100%',
        paddingVertical: 12,
        paddingHorizontal: SPACING.md,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
      }}
      accessibilityRole="text"
      // Con la cobertura live esta insignia anuncia la póliza real del viaje;
      // su etiqueta para lectores de pantalla dice exactamente eso.
      accessibilityLabel={a11yLabel}
    >
      <View
        style={{
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: RADIUS.full,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CoverageMark width={8} height={8} testID={markTestID} />
      </View>
      <View style={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.86}
          style={{
            fontSize: 15,
            lineHeight: 20,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {t('insurance.provider_tagline')}
        </Text>
        <Text
          numberOfLines={1}
          style={{
            marginTop: 2,
            fontSize: 13,
            lineHeight: 18,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {t('insurance.provider_short_included_soon')}
        </Text>
      </View>
    </View>
  )
}

function CoverageMark({
  width,
  height,
  testID,
}: {
  width: number
  height: number
  testID: string
}) {
  return (
    <View
      testID={testID}
      style={{
        width,
        height,
        borderRadius: RADIUS.full,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: COLORS.coral.DEFAULT,
      }}
    />
  )
}
